import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG } from 'app/app.config';
import {
    LoginRequest,
    RefreshTokenRequest,
    DeleteUserRequest,
    CreateUserRequest,
    UpdateUserRequest,
    MeasurementUploadRequest,
    CreateLocationRequest,
    UpdateLocationRequest,
    DeleteLocationRequest,
    CreateTenantRequest,
    UpdateTenantRequest,
    TenantUserRequest,
    AddUserToTenantByEmailRequest,
    MeasurementExportRequest,
    CreateSavedFilterRequest,
    MoveMeasurementsRequest,
    FilterRequest,
    CheckFirmwareVersionRequest,
    VerifyFirmwareUpgradeRequest,
    NotifyFirmwareUploadRequest
} from './requests';
import { DeleteEndPoints, GetEndPoints, PostEndPoints, PutEndPoints } from './endpoints';
import {
    CreateUserResponse,
    GetUsersResponse,
    UpdateUserResponse,
    DashboardResponse,
    GetUserResponse,
    GetMeasurementResponse,
    UploadResponse,
    CreateLocationResponse,
    UpdateLocationResponse,
    GetLocationsResponse,
    GetLocationResponse,
    GetRegionsResponse,
    GetDevicesResponse,
    GetTenantsResponse,
    GetTenantResponse,
    GetDeviceResponse,
    SavedFilterResponse,
    GetSavedFiltersResponse,
    MeasurementComparisonResponse,
    MeasurementComparisonGroupedItem,
    BulkExportJobResponse,
    PaginatedResponse,
    CheckFirmwareVersionResponse,
    FirmwareFilesResponse,
    GetFirmwareVersionsResponse,
    GetFirmwareVersionResponse,
    VerifyFirmwareUpgradeResponse,
    NotifyFirmwareUploadResponse,
    MeasurementStatisticalDistributionResponse,
    SignalQualityPredictionResponse
} from './responses';
import { ExportType, GroupBy, Predicate } from 'app/enums';
import { MeasurementData } from 'app/features/measurement/models/measurement-view.types';
import { UserSettings } from './user-settings';

@Injectable({
    providedIn: 'root'
})
export class ApiClientService {
    private readonly httpClient = inject(HttpClient);

    public readonly apiBaseUrl = APP_CONFIG.apiBaseUrl;

    public login$(loginRequest: LoginRequest) {
        return this.httpClient.post(`${this.apiBaseUrl}${PostEndPoints.Login}`, loginRequest);
    }

    public refreshToken$(refreshTokenRequest: RefreshTokenRequest) {
        return this.httpClient.post(`${this.apiBaseUrl}${PostEndPoints.RefershToken}`, refreshTokenRequest);
    }

    public changeRepresentation$(representationId: string) {
        return this.httpClient.post(`${this.apiBaseUrl}${PostEndPoints.ChangeRepresentation}`, { representationId });
    }

    public logout$() {
        return this.httpClient.post(`${this.apiBaseUrl}${PostEndPoints.Logout}`, {});
    }

    public sendPasswordResetCode$(value: string) {
        return this.httpClient.post(`${this.apiBaseUrl}${PostEndPoints.SendPasswordResetCode}`, { email: value });
    }

    public resetPassword$(arg0: { email: string; code: string; newPassword: string }) {
        return this.httpClient.post(`${this.apiBaseUrl}${PostEndPoints.ResetPassword}`, arg0);
    }

    public createUser$(request: CreateUserRequest) {
        return this.httpClient.post<CreateUserResponse>(`${this.apiBaseUrl}${PostEndPoints.User}`, request);
    }

    public getUsers$() {
        return this.httpClient.get<GetUsersResponse>(`${this.apiBaseUrl}${GetEndPoints.Users}`);
    }

    public getUserSettings$() {
        return this.httpClient.get<UserSettings>(`${this.apiBaseUrl}${GetEndPoints.UserSettings}`);
    }

    public updateUserSettings$(settings: UserSettings) {
        return this.httpClient.put<UserSettings>(`${this.apiBaseUrl}${PutEndPoints.UserSettings}`, settings);
    }

    public getUser$(id: string) {
        return this.httpClient.get<GetUserResponse>(
            `${this.apiBaseUrl}${GetEndPoints.UserId.replace('{user-id}', id)}`
        );
    }

    public updateUser$(request: UpdateUserRequest) {
        return this.httpClient.put<UpdateUserResponse>(
            `${this.apiBaseUrl}${PutEndPoints.User.replace('{user-id}', request.userId)}`,
            request
        );
    }

    public deleteUser$(request: DeleteUserRequest) {
        return this.httpClient.delete<DeleteUserRequest>(
            `${this.apiBaseUrl}${DeleteEndPoints.User.replace('{user-id}', request.userId)}`
        );
    }

    public getDashboard$() {
        return this.httpClient.get<DashboardResponse>(`${this.apiBaseUrl}${GetEndPoints.Dashboard}`);
    }

    public updateAnnotation$(measurementId: string, request: { annotation: string }) {
        return this.httpClient.put<UpdateUserResponse>(
            `${this.apiBaseUrl}${PutEndPoints.MeasurementAnnotation.replace('{measurement-id}', measurementId)}`,
            request
        );
    }

    public updateMarkerAnnotation$(measurementId: string, markerNumber: number, request: { annotation: string }) {
        return this.httpClient.put<UpdateUserResponse>(
            `${this.apiBaseUrl}${PutEndPoints.MarkerAnnotation.replace('{measurement-id}', measurementId).replace('{marker-number}', markerNumber.toString())}`,
            request
        );
    }

    public uploadMeasurement$(request: MeasurementUploadRequest) {
        return this.httpClient.post<UploadResponse>(`${this.apiBaseUrl}${PostEndPoints.Upload}`, request);
    }

    public getMeasurements$(filterRequest: FilterRequest) {
        const sortParam = filterRequest.sortKey
            ? `${filterRequest.sortKey} ${filterRequest.sortAscending ? 'asc' : 'desc'}`
            : '';
        const searchParam = filterRequest.search ? `&search=${encodeURIComponent(filterRequest.search)}` : '';
        return this.httpClient.get<PaginatedResponse<GetMeasurementResponse>>(
            `${this.apiBaseUrl}${GetEndPoints.Measurements}?predicates=${JSON.stringify(filterRequest.predicates)}&page=${filterRequest.page}&pageSize=${filterRequest.pageSize}&sort=${sortParam}${searchParam}`
        );
    }

    public getMeasurementComparisonByFilters$(predicates: Predicate<string | boolean>[]) {
        return this.httpClient.get<MeasurementComparisonResponse>(
            `${this.apiBaseUrl}${GetEndPoints.MeasurementsComparison}?predicates=${JSON.stringify(predicates)}`
        );
    }

    public getMeasurementComparisonGrouped$(predicates: Predicate<string | boolean>[], groupBy: GroupBy) {
        return this.httpClient.get<MeasurementComparisonGroupedItem[]>(
            `${this.apiBaseUrl}${GetEndPoints.MeasurementsComparisonGrouped.replace('{group-by}', groupBy.toString())}?predicates=${JSON.stringify(predicates)}`
        );
    }

    public getMeasurementStatisticalDistribution$(predicates: Predicate<string | boolean>[]) {
        return this.httpClient.get<MeasurementStatisticalDistributionResponse[]>(
            `${this.apiBaseUrl}${GetEndPoints.MeasurementStatisticalDistribution}?predicates=${JSON.stringify(predicates)}`
        );
    }

    public getMeasurementFinalResult$(measurementId: string) {
        return this.httpClient.get<MeasurementData>(
            `${this.apiBaseUrl}${GetEndPoints.MeasurementFinalResult.replace('{measurement-id}', measurementId)}`
        );
    }

    public downloadMeasurementExportFile$(measurementId: string, fileType: ExportType) {
        return this.httpClient.get(
            `${this.apiBaseUrl}${GetEndPoints.MeasurementExportFile.replace('{measurement-id}', measurementId).replace(
                '{file-type}',
                fileType.toString()
            )}`,
            {
                responseType: 'blob',
                observe: 'response'
            }
        );
    }

    public downloadMeasurementsExportFile$(request: MeasurementExportRequest) {
        return this.httpClient.post(`${this.apiBaseUrl}${PostEndPoints.MeasurementsExportFile}`, request, {
            responseType: 'blob',
            observe: 'response'
        });
    }

    public createBulkExportJob$(request: MeasurementExportRequest) {
        return this.httpClient.post<BulkExportJobResponse>(
            `${this.apiBaseUrl}${PostEndPoints.MeasurementsBulkExport}`,
            request
        );
    }

    public getBulkExportJobStatus$(jobId: string) {
        return this.httpClient.get<BulkExportJobResponse>(
            `${this.apiBaseUrl}${GetEndPoints.MeasurementsBulkExportJob.replace('{job-id}', jobId)}`
        );
    }

    public getFirmwareVersions$(): Observable<GetFirmwareVersionsResponse> {
        return this.httpClient.get<GetFirmwareVersionsResponse>(`${this.apiBaseUrl}${GetEndPoints.FirmwareVersions}`);
    }

    public getFirmwareVersion$(versionId: string): Observable<GetFirmwareVersionResponse> {
        return this.httpClient.get<GetFirmwareVersionResponse>(
            `${this.apiBaseUrl}${GetEndPoints.FirmwareVersion.replace('{version-id}', versionId)}`
        );
    }

    public checkFirmwareVersion$(request: CheckFirmwareVersionRequest): Observable<CheckFirmwareVersionResponse> {
        return this.httpClient.post<CheckFirmwareVersionResponse>(
            `${this.apiBaseUrl}${PostEndPoints.CheckFirmwareVersion}`,
            request
        );
    }

    public getFirmwareFiles$(versionId: string): Observable<FirmwareFilesResponse> {
        return this.httpClient.get<FirmwareFilesResponse>(
            `${this.apiBaseUrl}${GetEndPoints.FirmwareFiles.replace('{version-id}', versionId)}`
        );
    }

    public downloadFirmwareFile$(fileId: string): Observable<Blob> {
        return this.httpClient.get(
            `${this.apiBaseUrl}${GetEndPoints.FirmwareFileDownload.replace('{file-id}', fileId)}`,
            { responseType: 'blob' }
        );
    }

    public verifyFirmwareUpgrade$(request: VerifyFirmwareUpgradeRequest): Observable<VerifyFirmwareUpgradeResponse> {
        return this.httpClient.post<VerifyFirmwareUpgradeResponse>(
            `${this.apiBaseUrl}${PostEndPoints.VerifyFirmwareUpgrade}`,
            request
        );
    }

    public notifyFirmwareUpload$(request: NotifyFirmwareUploadRequest): Observable<NotifyFirmwareUploadResponse> {
        return this.httpClient.post<NotifyFirmwareUploadResponse>(
            `${this.apiBaseUrl}${PostEndPoints.NotifyFirmwareUpload}`,
            request
        );
    }

    public createFirmwareVersion(formData: FormData) {
        return this.httpClient.post(`${this.apiBaseUrl}${PostEndPoints.Firmware}`, formData);
    }

    public updateFirmwareVersion(versionId: string, formData: FormData) {
        return this.httpClient.put(
            `${this.apiBaseUrl}${PutEndPoints.Firmware.replace('{version-id}', versionId)}`,
            formData
        );
    }

    public deleteFirmwareVersion(versionId: string) {
        return this.httpClient.delete(
            `${this.apiBaseUrl}${DeleteEndPoints.Firmware.replace('{version-id}', versionId)}`
        );
    }

    public deleteMeasurement$(measurementId: string) {
        return this.httpClient.delete(
            `${this.apiBaseUrl}${DeleteEndPoints.Measurement.replace('{measurement-id}', measurementId)}`
        );
    }

    public deleteMeasurements$(measurementIds: string[]) {
        return this.httpClient.post(`${this.apiBaseUrl}${PostEndPoints.MeasurementsBulkDelete}`, {
            measurementIds
        });
    }

    public moveMeasurements$(request: MoveMeasurementsRequest) {
        return this.httpClient.put(`${this.apiBaseUrl}${PutEndPoints.MeasurementsLocation}`, request);
    }

    public getDevices$() {
        return this.httpClient.get<GetDevicesResponse>(`${this.apiBaseUrl}${GetEndPoints.Devices}`);
    }

    public getDevice$(id: string) {
        return this.httpClient.get<GetDeviceResponse>(
            `${this.apiBaseUrl}${GetEndPoints.Device.replace('{device-id}', id)}`
        );
    }

    public clearDeviceCalibration$(deviceId: string) {
        return this.httpClient.delete(
            `${this.apiBaseUrl}${DeleteEndPoints.DeviceCalibration.replace('{device-id}', deviceId)}`
        );
    }

    public downloadDeviceLogFile$(deviceId: string, logFileId: string) {
        return this.httpClient.get(
            `${this.apiBaseUrl}${GetEndPoints.DeviceLogFile.replace('{device-id}', deviceId).replace('{log-file-id}', logFileId)}`,
            {
                responseType: 'blob',
                observe: 'response'
            }
        );
    }

    public createTenant$(request: CreateTenantRequest) {
        return this.httpClient.post<GetTenantResponse>(`${this.apiBaseUrl}${PostEndPoints.Tenant}`, request);
    }

    public updateTenant$(request: UpdateTenantRequest) {
        return this.httpClient.put<GetTenantResponse>(
            `${this.apiBaseUrl}${PutEndPoints.Tenant.replace('{tenant-id}', request.tenantId)}`,
            request
        );
    }

    public deleteTenant$(tenantId: string) {
        return this.httpClient.delete(`${this.apiBaseUrl}${DeleteEndPoints.Tenant.replace('{tenant-id}', tenantId)}`);
    }

    public getTenants$() {
        return this.httpClient.get<GetTenantsResponse>(`${this.apiBaseUrl}${GetEndPoints.Tenants}`);
    }

    public getTenantUsers$(tenantId: string) {
        return this.httpClient.get<GetUsersResponse>(
            `${this.apiBaseUrl}${GetEndPoints.TenantUsers.replace('{tenant-id}', tenantId)}`
        );
    }

    public getAddableUsers$(tenantId: string) {
        return this.httpClient.get<GetUsersResponse>(
            `${this.apiBaseUrl}${GetEndPoints.AddableTenantUsers.replace('{tenant-id}', tenantId)}`
        );
    }

    public addUserToTenant$(tenantId: string, request: TenantUserRequest) {
        return this.httpClient.post(
            `${this.apiBaseUrl}${PostEndPoints.UserToTenant.replace('{tenant-id}', tenantId)}`,
            request
        );
    }

    public addUserToTenantByEmail$(request: AddUserToTenantByEmailRequest) {
        return this.httpClient.post(`${this.apiBaseUrl}${PostEndPoints.AddUserToTenantByEmail}`, request);
    }

    public removeUserFromTenant$(tenantId: string, userId: string) {
        const tenant = tenantId ?? '00000000-0000-0000-0000-000000000000'; // Use a default value if tenantId is null or undefined
        return this.httpClient.delete(
            `${this.apiBaseUrl}${DeleteEndPoints.UserFromTenant.replace('{tenant-id}', tenant).replace('{user-id}', userId)}`
        );
    }

    public createLocation$(request: CreateLocationRequest) {
        return this.httpClient.post<CreateLocationResponse>(`${this.apiBaseUrl}${PostEndPoints.Location}`, request);
    }

    public getLocations$() {
        return this.httpClient.get<GetLocationsResponse>(`${this.apiBaseUrl}${GetEndPoints.Locations}`);
    }

    public getRegions$() {
        return this.httpClient.get<GetRegionsResponse>(`${this.apiBaseUrl}${GetEndPoints.Regions}`);
    }

    public getLocation$(id: string) {
        return this.httpClient.get<GetLocationResponse>(
            `${this.apiBaseUrl}${GetEndPoints.LocationId.replace('{location-id}', id)}`
        );
    }

    public updateLocation$(request: UpdateLocationRequest) {
        return this.httpClient.put<UpdateLocationResponse>(
            `${this.apiBaseUrl}${PutEndPoints.Location.replace('{location-id}', request.locationId)}`,
            request
        );
    }

    public deleteLocation$(request: DeleteLocationRequest) {
        return this.httpClient.delete(
            `${this.apiBaseUrl}${DeleteEndPoints.Location.replace('{location-id}', request.locationId)}`
        );
    }

    public getSavedFilters$() {
        return this.httpClient.get<GetSavedFiltersResponse>(`${this.apiBaseUrl}${GetEndPoints.SavedFilters}`);
    }

    public createSavedFilter$(request: CreateSavedFilterRequest) {
        return this.httpClient.post<SavedFilterResponse>(`${this.apiBaseUrl}${PostEndPoints.SavedFilter}`, request);
    }

    public deleteSavedFilter$(id: string) {
        return this.httpClient.delete(
            `${this.apiBaseUrl}${DeleteEndPoints.SavedFilter.replace('{saved-filter-id}', id)}`
        );
    }

    public getSignalQualityPrediction$(
        predicates: Predicate<string | boolean>[] = []
    ): Observable<SignalQualityPredictionResponse> {
        return this.httpClient.get<SignalQualityPredictionResponse>(
            `${this.apiBaseUrl}${GetEndPoints.MeasurementSignalQualityPrediction}?predicates=${JSON.stringify(predicates)}`,
            {}
        );
    }
}
